import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip as WmsTooltip } from "react-tooltip";
import { listProductForDropship, getDropshipMarginConfig } from "../../../store/actions/dropship.action";

const FULFILLMENT_HELP =
    "Your promised handling time — the maximum number of days you commit to dispatching the package from your warehouse after a dropshipper places an order. This is shown to dropshippers in the catalog so they can set realistic delivery expectations for their customers.";

export default function DropshipListingModal({ show, onHide, product, onSuccess }) {
    const dispatch = useDispatch();
    const { marginConfigs } = useSelector((state) => state.dropshipReducer);

    useEffect(() => {
        if (show) dispatch(getDropshipMarginConfig({}));
    }, [show]);

    const globalMargin = marginConfigs.find((c) => c.scope === "Global") || { flatAmount: 0, percentAmount: 0 };
    const marginFlat = Number.parseFloat(globalMargin.flatAmount) || 0;
    const marginPercent = Number.parseFloat(globalMargin.percentAmount) || 0;
    // Compute the seller's actual on-hand stock for this row.
    // Mirrors ProductList.js convention: totalQuantity (inventory) is "all", quantity is "returns".
    // Sellable inventory = totalQuantity - quantity (when totalQuantity > 0), else fall back to quantity.
    const onHandQty = useMemo(() => {
        if (!product) return 0;
        const total = Number(product.totalQuantity) || 0;
        const returns = Number(product.quantity) || 0;
        if (total > 0) return Math.max(total - returns, 0);
        return returns;
    }, [product]);

    const [wholesalePrice, setWholesalePrice] = useState("");
    const [shippingPaidBy, setShippingPaidBy] = useState("Supplier");
    const [fulfillmentDays, setFulfillmentDays] = useState("3");
    const [returnPolicy, setReturnPolicy] = useState("");
    const [availableQuantity, setAvailableQuantity] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const requestedQty = Number.parseInt(availableQuantity, 10) || 0;
    const exceedsStock = requestedQty > onHandQty;

    const handleSubmit = async () => {
        if (!wholesalePrice || Number.parseFloat(wholesalePrice) <= 0) {
            toast.error("Please enter a valid wholesale price.");
            return;
        }
        if (!fulfillmentDays || Number.parseInt(fulfillmentDays, 10) <= 0) {
            toast.error("Please enter valid fulfillment days.");
            return;
        }
        if (!availableQuantity || requestedQty <= 0) {
            toast.error("Please enter the quantity you're making available for dropshipping.");
            return;
        }
        if (onHandQty <= 0) {
            toast.error("You have no on-hand stock for this product. Restock before listing for dropshipping.");
            return;
        }
        if (exceedsStock) {
            toast.error(`Cannot list ${requestedQty} units. You only have ${onHandQty} on hand.`);
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                inventoryProductId: product.id || product.Id,
                wholesalePrice: Number.parseFloat(wholesalePrice),
                shippingPaidBy,
                fulfillmentDays: Number.parseInt(fulfillmentDays, 10),
                returnPolicy,
                availableQuantity: requestedQty,
            };
            const result = await listProductForDropship(payload);
            const apiResult = result.data?.EntityList?.[0];
            if (apiResult?.success) {
                toast.success("Product listed for dropshipping.");
                onSuccess?.();
                onHide();
            } else {
                // Server-side cap message comes through here too
                toast.error(apiResult?.message || "Failed to list product.");
            }
        } catch (err) {
            toast.error("Failed to list product for dropshipping.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>List for Dropshipping</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-3" style={{ fontSize: "13px", color: "#666" }}>
                    <strong>{product?.productSKU || product?.ProductSKU || product?.productSku}</strong>
                    {` - ${product?.productName || product?.name || product?.Name || ""}`}
                </p>

                {/* On-hand stock banner — standard Bootstrap alert classes */}
                <div className={`alert ${onHandQty > 0 ? "alert-success" : "alert-danger"} py-2 mb-3`} role="alert">
                    <i className={`fa fa-${onHandQty > 0 ? "check-circle" : "exclamation-triangle"} mr-2`} />
                    <span>On-hand stock for this product: <strong>{onHandQty}</strong> units.</span>
                    {onHandQty <= 0 && <span> You cannot list a product with zero stock.</span>}
                </div>

                <Form.Group className="mb-3">
                    <Form.Label>Wholesale Price ($)</Form.Label>
                    <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Enter wholesale price"
                        value={wholesalePrice}
                        onChange={(e) => setWholesalePrice(e.target.value)}
                    />
                </Form.Group>

                {/* Platform margin preview — supplier-side disclosure of the 3PL cut */}
                {Number.parseFloat(wholesalePrice) > 0 && (
                    <div
                        className="margin-preview"
                        style={{
                            background: "#f8f9fa",
                            border: "1px solid #e2e6ea",
                            borderRadius: 6,
                            padding: "10px 14px",
                            marginBottom: 16,
                            fontSize: 13,
                        }}
                    >
                        <div className="d-flex justify-content-between" style={{ color: "#555" }}>
                            <span>Platform Margin per Unit</span>
                            <span>${(marginFlat + (Number.parseFloat(wholesalePrice) * marginPercent / 100)).toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between" style={{ marginTop: 4, fontWeight: 600 }}>
                            <span>Your Net per Unit</span>
                            <span>${(Number.parseFloat(wholesalePrice) - (marginFlat + (Number.parseFloat(wholesalePrice) * marginPercent / 100))).toFixed(2)}</span>
                        </div>
                        <div style={{ marginTop: 6, color: "#888", fontSize: 11 }}>
                            3PL Vision charges a per-order margin (Flat ${marginFlat.toFixed(2)} + {marginPercent.toFixed(2)}% of wholesale). The dropshipper&apos;s effective wholesale is your price plus this margin; you receive your full wholesale price on settlement.
                        </div>
                    </div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Quantity Available for Dropshipping</Form.Label>
                    <Form.Control
                        type="number"
                        min="1"
                        max={onHandQty}
                        placeholder={`Max ${onHandQty}`}
                        value={availableQuantity}
                        onChange={(e) => setAvailableQuantity(e.target.value)}
                        isInvalid={exceedsStock}
                        disabled={onHandQty <= 0}
                    />
                    {exceedsStock && (
                        <Form.Control.Feedback type="invalid">
                            Cannot exceed on-hand stock ({onHandQty}).
                        </Form.Control.Feedback>
                    )}
                    <Form.Text className="text-muted">
                        Dropshippers will reserve units from this pool. Unsold reservations return to your pool when they expire.
                    </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Shipping Paid By</Form.Label>
                    <Form.Select value={shippingPaidBy} onChange={(e) => setShippingPaidBy(e.target.value)}>
                        <option value="Supplier">Supplier</option>
                        <option value="Dropshipper">Dropshipper</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>
                        Fulfillment Time (Days){" "}
                        <i
                            id="dropshipListingFulfillmentInfo"
                            className="fa fa-info-circle"
                            aria-hidden="true"
                            style={{ color: "#666", cursor: "help" }}
                        />
                        <WmsTooltip
                            anchorId="dropshipListingFulfillmentInfo"
                            className="tooltipTxt"
                            place="top"
                            content={FULFILLMENT_HELP}
                        />
                    </Form.Label>
                    <Form.Control
                        type="number"
                        min="1"
                        placeholder="e.g. 3"
                        value={fulfillmentDays}
                        onChange={(e) => setFulfillmentDays(e.target.value)}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>Return Policy</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Describe your return policy (optional)"
                        value={returnPolicy}
                        onChange={(e) => setReturnPolicy(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button className="cus-primary-transparent-btn" onClick={onHide} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    className="cus-seconday-bg-btn"
                    onClick={handleSubmit}
                    disabled={submitting || onHandQty <= 0 || exceedsStock}
                >
                    {submitting ? "Listing..." : "List Product"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

DropshipListingModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        productSKU: PropTypes.string,
        ProductSKU: PropTypes.string,
        productSku: PropTypes.string,
        productName: PropTypes.string,
        Name: PropTypes.string,
        name: PropTypes.string,
        totalQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
};

DropshipListingModal.defaultProps = {
    onSuccess: undefined,
};
